import React, { useState, useEffect } from 'react';
import { Box, Grid, makeStyles, MenuItem, TextField, Theme, Typography } from '@material-ui/core';
import { addDays, format } from 'date-fns';

import {
  ExportReportRequest,
  getSiteEquipment,
  isDisplayTag,
  Site,
  TagType,
} from '../../../models';

import { TagTree } from '../site/TagTree';
import MainModal from 'src/components/ui/MainModal';
import DeliverySettings from './DeliverySettings';

import { dividerColor } from 'src/data/colors';

const useStyles = makeStyles((theme: Theme) => ({
  timeContainer: {
    border: `1px solid ${dividerColor}`,
    borderRadius: 8,
    fontSize: 20,
    marginBottom: 16,
    padding: 16,
  },
  timeContent: {
    marginTop: 8,
  },
  timeAlignInput: {
    marginTop: 16,
  },
  tagsCard: {
    height: 480,
    border: `1px solid ${dividerColor}`,
    borderRadius: 8,
    fontSize: 20,
    marginBottom: 16,
    padding: 16,
    overflow: 'hidden',
  },
  tagsContainer: {
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
    paddingBottom: 28,
  },
}));

const TIME_ALIGNMENT_OPTIONS = [1, 5, 15, 30, 60];

export interface Props {
  closeForm: () => void;
  email: string;
  onSubmit: (values: ExportReportRequest) => void;
  site: Site;
  siteId: number;
  submitCounter: number;
  users: string[];
}

const ExportConfigurationForm = ({
  closeForm,
  email,
  onSubmit,
  site,
  siteId,
  submitCounter,
  users,
}: Props) => {
  const [historicalTS, setHistoricalTS] = useState(format(addDays(new Date(), -2), 'yyyy-MM-dd'));
  const [modernTS, setModernTS] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [recipients, setRecipients] = useState<string[]>([]);
  const [tags, setTags] = useState<string[]>([]);
  const [timeAlignMinutes, setTimeAlignMinutes] = useState<number | undefined>(undefined);
  const [needsTimeAlignment, setNeedsTimeAlignment] = useState(false);

  const classes = useStyles();

  useEffect(() => {
    setRecipients([]);
    setTags([]);
    setTimeAlignMinutes(undefined);
    setNeedsTimeAlignment(false);
  }, [submitCounter]);

  const handleAddRecipient = (newRecipients: string[]) => {
    setRecipients(newRecipients);
  };

  const handleRemoveRecipient = (emailAddress: string) => {
    setRecipients(recipients.filter((email) => email !== emailAddress));
  };

  const handleChangeHistoricalTS = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHistoricalTS(event.currentTarget.value);
  };

  const handleChangeModernTS = (event: React.ChangeEvent<HTMLInputElement>) => {
    setModernTS(event.currentTarget.value);
  };

  const handleTimeAlignChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setTimeAlignMinutes(value === '' ? undefined : parseInt(value, 10));
  };

  const handleSelectTag = (tagId: string) => {
    const newTags = tags.includes(tagId) ? tags.filter((i) => i !== tagId) : [...tags, tagId];

    const needsTimeAlignment =
      getSiteEquipment(site).filter((equipment) => {
        return newTags.some((tag) => equipment.tags.map((t) => t.id).includes(tag));
      }).length > 1;

    setTags(newTags);
    setNeedsTimeAlignment(needsTimeAlignment);
    if (needsTimeAlignment && !timeAlignMinutes) {
      setTimeAlignMinutes(1);
    }
  };

  const handleExportReport = () => {
    if (!historicalTS || !modernTS) {
      return;
    }

    const exportReportRequest = {
      siteId,
      additionalRecipientEmails: recipients,
      tagNames: tags,
      historicalTS,
      modernTS,
      timeAlignMinutes,
      rawTimestampsOnly: timeAlignMinutes ? false : true,
    };
    onSubmit(exportReportRequest);
  };

  const isValid = tags.length > 0 && historicalTS && modernTS;

  return (
    <MainModal
      disabledPrimaryButton={!isValid}
      onClose={closeForm}
      onPrimaryAction={handleExportReport}
      onSecondaryAction={closeForm}
      primaryButtonLabel="Export"
      secondaryButtonLabel="Cancel"
      title="Configure Report"
      width={480}
      isOpen
    >
      <Box style={{ height: '100%' }}>
        <Box>
          <Box>
            <Typography variant="subtitle1">Custom Export</Typography>
          </Box>
          <Box className={classes.timeContainer}>
            Set analysis time range
            <Grid container spacing={2} className={classes.timeContent}>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="date"
                  label="From"
                  value={historicalTS || ''}
                  error={!historicalTS}
                  inputProps={{
                    min: site.commissionedOn
                      ? format(new Date(site.commissionedOn), 'yyyy-MM-dd')
                      : '',
                  }}
                  helperText={!historicalTS && 'From date is required'}
                  onChange={handleChangeHistoricalTS}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  size="small"
                  fullWidth
                  type="date"
                  label="To"
                  value={modernTS || ''}
                  error={!modernTS}
                  inputProps={{
                    max: format(addDays(new Date(), 1), 'yyyy-MM-dd'),
                  }}
                  helperText={!modernTS && 'To date is required'}
                  onChange={handleChangeModernTS}
                />
              </Grid>
            </Grid>
            <TextField
              SelectProps={{
                displayEmpty: true,
              }}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              size="small"
              fullWidth
              label="Time Alignment (minutes)"
              onChange={handleTimeAlignChange}
              value={timeAlignMinutes || ''}
              select
              className={classes.timeAlignInput}
            >
              <MenuItem disabled={needsTimeAlignment} value="">
                None
              </MenuItem>
              {TIME_ALIGNMENT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box>
            <Box className={classes.tagsCard}>
              <Typography variant="subtitle1">Select Tags</Typography>
              <Box className={classes.tagsContainer}>
                <TagTree
                  site={site}
                  onTagSelect={handleSelectTag}
                  selectedTags={tags}
                  tagFilter={(tag) => {
                    return isDisplayTag(tag) || tag.tagType === TagType.Control;
                  }}
                />
              </Box>
            </Box>
            <DeliverySettings
              recipients={recipients}
              handleAddRecipient={handleAddRecipient}
              handleRemoveRecipient={handleRemoveRecipient}
              users={users}
              email={email}
            />
          </Box>
        </Box>
      </Box>
    </MainModal>
  );
};

export default ExportConfigurationForm;
